import z from 'zod'
import { AsyncFn, AsyncTask, LocalDateString } from '../../../common'
import { guidSchema } from '../../../contracts'
import { AccountGuid } from '../../Accounts/Account'
import { Address } from '../../Address/Address'
import { ForCompany, ForCompanyUser } from '../../Company/Company'
import { bzOptional, Guid } from '../../common-schemas'
import { optionalEmailAddressValueSchema, ToEmailAddressContainer } from '../../Email/EmailTypes'
import { InvoiceV2Enriched } from '../../InvoicesV2/InvoicesV2'

export enum InvoiceTerm {
  DUE_ON_RECEIPT = 'DUE_ON_RECEIPT',
  NET_7 = 'NET_7',
  NET_10 = 'NET_10',
  NET_14 = 'NET_14',
  NET_15 = 'NET_15',
  NET_30 = 'NET_30',
  NET_45 = 'NET_45',
  NET_60 = 'NET_60',
  AUTO = 'AUTO',
}

export const invoiceTermDisplayNames = {
  [InvoiceTerm.DUE_ON_RECEIPT]: 'Due on Receipt',
  [InvoiceTerm.NET_7]: 'Net 7',
  [InvoiceTerm.NET_10]: 'Net 10',
  [InvoiceTerm.NET_14]: 'Net 14',
  [InvoiceTerm.NET_15]: 'Net 15',
  [InvoiceTerm.NET_30]: 'Net 30',
  [InvoiceTerm.NET_45]: 'Net 45',
  [InvoiceTerm.NET_60]: 'Net 60',
  [InvoiceTerm.AUTO]: 'Automatic',
}

export const invoiceTermDays = {
  [InvoiceTerm.DUE_ON_RECEIPT]: 0,
  [InvoiceTerm.NET_7]: 7,
  [InvoiceTerm.NET_10]: 10,
  [InvoiceTerm.NET_14]: 14,
  [InvoiceTerm.NET_15]: 15,
  [InvoiceTerm.NET_30]: 30,
  [InvoiceTerm.NET_45]: 45,
  [InvoiceTerm.NET_60]: 60,
  [InvoiceTerm.AUTO]: 0,
}

export const formatInvoiceTerm = (invoiceTerm: InvoiceTerm): string => invoiceTermDisplayNames[invoiceTerm]

export type InvoiceGuid = Guid

export const sendInvoiceRequestSchema = z.object({
  invoiceGuid: guidSchema,
  to: optionalEmailAddressValueSchema,
})

export const sendInvoicePdfRequestSchema = z.object({
  invoiceGuid: guidSchema,
  to: optionalEmailAddressValueSchema,
  subject: z.string(),
  description: z.string(),
  checklistInstanceGuids: z.array(guidSchema).optional(),
})

export type SendInvoicePdfRequest = z.infer<typeof sendInvoicePdfRequestSchema>

export type InvoiceGuidContainer = { invoiceGuid: InvoiceGuid }
export const InvoiceEntityTypeName = 'Invoice' as const

export type AbridgedInvoiceMetadata = {
  invoiceGuid: InvoiceGuid
  accountGuid: AccountGuid
  billingContactGuid?: Guid
  displayId: string
  referenceNumber: string
  status: InvoiceStatuses
  serviceCompletionDate?: LocalDateString
}

export type AbridgedInvoiceMetadataWithAmountDue = AbridgedInvoiceMetadata & {
  amountDueUsc: number
}

export type InvoiceEmailRequest = InvoiceGuidContainer &
  Partial<ToEmailAddressContainer> & {
    subject?: string
    description?: string
    checklistInstanceGuids?: string[]
  }

export type InvoiceV2Emailer = AsyncFn<ForCompanyUser<InvoiceEmailRequest>, void>

export type InvoiceV2HtmlGenerator = AsyncFn<InvoiceV2Enriched, string>

export type InvoiceV2PdfGenerator = AsyncFn<InvoiceV2Enriched, Buffer>

export type InvoiceServiceAddressReader = AsyncFn<ForCompany<InvoiceGuidContainer>, Address | undefined>

export type InvoiceReminderSendTask = AsyncTask

/** @deprecated */
export enum InvoiceStatuses {
  DRAFT = 'Draft',
  CREATED = 'Created',
  PRESENTED = 'Presented',
  ACCEPTED = 'Accepted',
  REJECTED = 'Rejected',
  FULLY_PAID = 'Fully Paid',
  VOID = 'Void',
  REFUNDED = 'Refunded',
}

/** Used to straddle InvoicesV1 and InvoicesV2 for code that relies on this type */
export type AbridgedInvoiceViewModel = {
  invoiceGuid: Guid
  displayId: string
  totalAmountUsd: number
  invoiceStatus: InvoiceStatuses
}

/** @deprecated */
export const SendInvoiceSchema = z.object({
  invoiceGuid: guidSchema,
  deliveryMethod: z.enum(['EMAIL', 'SMS']),
  body: z.string(),
  to: z.string(),
  subject: bzOptional(z.string()),
  paymentLink: z.string(),
})
export type SendInvoiceRequest = z.infer<typeof SendInvoiceSchema>
